export type OrgTimeToRealizeScheduleWorkPackage = Readonly<{
  workPackageId: string;
  predecessorIds: readonly string[];
  durationDays: number;
}>;

export type OrgTimeToRealizeSchedule = Readonly<{
  completionDays: ReadonlyMap<string, number>;
  criticalPathByWorkPackageId: ReadonlyMap<string, readonly string[]>;
}>;

export function buildOrgTimeToRealizeSchedule(
  workPackages: readonly OrgTimeToRealizeScheduleWorkPackage[]
): OrgTimeToRealizeSchedule | null {
  const known = new Set(workPackages.map((workPackage) => workPackage.workPackageId));
  const completionDays = new Map<string, number>();
  const criticalPathByWorkPackageId = new Map<string, readonly string[]>();
  while (completionDays.size < workPackages.length) {
    let progressed = false;
    for (const workPackage of workPackages) {
      if (completionDays.has(workPackage.workPackageId)) continue;
      if (workPackage.predecessorIds.some((id) => !known.has(id))) return null;
      if (workPackage.predecessorIds.some((id) => !completionDays.has(id))) continue;
      const criticalPredecessor = selectCriticalPredecessor(
        workPackage.predecessorIds,
        completionDays
      );
      const startDay = criticalPredecessor ? (completionDays.get(criticalPredecessor) ?? 0) : 0;
      completionDays.set(workPackage.workPackageId, startDay + workPackage.durationDays);
      criticalPathByWorkPackageId.set(
        workPackage.workPackageId,
        Object.freeze([
          ...(criticalPredecessor
            ? (criticalPathByWorkPackageId.get(criticalPredecessor) ?? [])
            : []),
          workPackage.workPackageId,
        ])
      );
      progressed = true;
    }
    if (!progressed) return null;
  }
  return Object.freeze({ completionDays, criticalPathByWorkPackageId });
}

function selectCriticalPredecessor(
  predecessorIds: readonly string[],
  completionDays: ReadonlyMap<string, number>
): string | undefined {
  return [...predecessorIds].sort(
    (left, right) => (completionDays.get(right) ?? 0) - (completionDays.get(left) ?? 0)
  )[0];
}
